import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { BedrijfSchema, FilterSchema } from '@/api';
import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluit } from '@/api/queries/besluit';
import { useAllGrondslagenInBesluiten } from '@/api/queries/besluitGrondslag/selectors/useAllGrondsalgenInBesluiten';
import { BesluitCardSkeleton } from '@/components/BesluitCard/BesluitCard';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { eStrings } from '@/constants/strings';
import { createInString } from '@/utils/createInString';
import { DateUtils } from '@/utils/dateUtils';

import { BesluitCardVigerend } from './-components/BesluitCardVigerend';
import { useHasSelectedBesluitFilters } from './-components/BesluitFilters';

/**
 * Search schema
 */
const searchSchema = z.object({
  p: z.number().optional().default(1),
  sort: z
    .enum(['oud-nieuw', 'nieuw-oud'])
    .default('nieuw-oud')
    .catch('nieuw-oud')
    .optional(),
  datumStart: z
    .string()
    .date()
    .catch(DateUtils.formatForInput(new Date()))
    .optional(),
  datumEinde: z
    .string()
    .date()
    .catch(DateUtils.formatForInput(new Date()))
    .optional(),
  type: z.string().optional(),
  vergunningdeel: z.array(z.string()).optional(),
  zaaknummer: z.string().optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

const convertToLocalDate = (date: string) => {
  const dateUTC = new Date(date);
  const dateLocal = new Date(
    dateUTC.getTime() + dateUTC.getTimezoneOffset() * 60000
  );

  return dateLocal;
};

const besluitenQueryOptions = (
  search: SearchParams,
  bedrijfLineageId: string
) => {
  const filter: FilterSchema[] = [
    {
      model: 'Besluit',
      field: 'Status',
      op: 'eq',
      value: 'Publicatie',
    },
    {
      model: 'Bedrijf',
      field: 'Lineage',
      op: 'eq',
      value: bedrijfLineageId,
    },
  ];

  if (search.type) {
    filter.push({
      model: 'Besluit',
      field: 'Type',
      op: 'eq',
      value: search.type,
    });
  }

  if (search.vergunningdeel?.length) {
    filter.push({
      model: 'BesluitGrondslag',
      field: 'Grondslag',
      op: 'in',
      value: createInString(search.vergunningdeel),
    });
  }

  if (search.datumStart) {
    /**
     * Because we store the date as: yyyy-mm-dd which is UTC, we need to convert it to the local timezone.
     */
    const dateStartLocal = convertToLocalDate(search.datumStart);

    filter.push({
      model: 'Besluit',
      field: 'Besluit_Datum',
      op: 'ge',
      value: DateUtils.formatForBE(dateStartLocal),
    });
  }

  if (search.datumEinde) {
    /**
     * Because we store the date as: yyyy-mm-dd which is UTC, we need to convert it to the local timezone.
     */
    const dateEndLocal = convertToLocalDate(search.datumEinde);

    filter.push({
      model: 'Besluit',
      field: 'Besluit_Datum',
      op: 'le',
      value: DateUtils.formatForBE(dateEndLocal),
    });
  }

  if (search.zaaknummer) {
    filter.push({
      model: 'Besluit',
      field: 'Zaaknummer',
      op: 'like',
      value: `%${search.zaaknummer.replace(/\s+/g, '%')}%`,
    });
  }

  const queryOptions = useBesluit.getOptions({
    page: search.p,
    size: 10,
    filter,
    sort: [
      {
        model: 'Besluit',
        field: 'Besluit_Datum',
        direction: search.sort === 'oud-nieuw' ? 'asc' : 'desc',
      },
    ],
  });

  return queryOptions;
};

const latestBedrijfQueryOptions = (lineageId: string) =>
  useLatestBedrijf.getOptions(lineageId);

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/bedrijf/_layout/besluiten'
)({
  validateSearch: searchSchema.parse,
  beforeLoad: () => ({
    getTitle: () => 'Besluiten en wijzigingen',
  }),
  loader: async ({ params, context: { queryClient } }) => {
    const [besluiten] = await Promise.all([
      queryClient.ensureQueryData(
        besluitenQueryOptions({ p: 1 }, params.bedrijfLineageId)
      ),
      queryClient.ensureQueryData(
        latestBedrijfQueryOptions(params.bedrijfLineageId)
      ),
    ]);

    if (besluiten.objects?.length) {
      await queryClient.ensureQueryData(
        useAllGrondslagenInBesluiten.queryOptions({
          besluiten: besluiten.objects,
        })
      );
    }
  },
  component: BesluitenComponent,
});

/**
 * Route Component
 */
function BesluitenComponent() {
  const { bedrijfLineageId } = Route.useParams();

  const bedrijf = useQuery(latestBedrijfQueryOptions(bedrijfLineageId));

  if (!bedrijf.data) return null;

  return (
    <section data-testid="bedrijfdasboard-tab-besluiten" className="py-5">
      <Results bedrijf={bedrijf.data} />
    </section>
  );
}

/**
 * Results
 */
const Results = ({ bedrijf }: { bedrijf: BedrijfSchema }) => {
  const search = Route.useSearch();
  const hasSelectedFilters = useHasSelectedBesluitFilters();

  const vigerendBesluiten = useQuery(
    besluitenQueryOptions(search, bedrijf.Lineage)
  );
  const besluitGrondslagen = useAllGrondslagenInBesluiten({
    besluiten: vigerendBesluiten.data?.objects ?? [],
  });

  if (vigerendBesluiten.isLoading || besluitGrondslagen.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <h2 className="style-h5-bold mb-2 text-gray-800">
          {eStrings.LBL_BESLUITEN_EN_WIJZIGINGEN}
        </h2>
        <BesluitCardSkeleton />
      </div>
    );
  }

  if (!vigerendBesluiten.data?.objects?.length) {
    return (
      <p className="style-body italic text-gray-700">
        {hasSelectedFilters
          ? 'Er zijn geen resultaten met de gekozen filters'
          : 'Er zijn nog geen besluiten of wijzigingen die getoond kunnen worden. Ook is er nog geen vigerende situatie beschikbaar.'}
      </p>
    );
  }

  return (
    <>
      <h2 className="style-h5-bold mb-2 text-gray-800">
        {eStrings.LBL_BESLUITEN_EN_WIJZIGINGEN}
      </h2>

      <ul className="flex flex-col gap-2">
        {vigerendBesluiten.data.objects.map((besluit) => {
          return (
            <li key={besluit.ID}>
              <BesluitCardVigerend
                headingAs="h3"
                bedrijfLineageId={bedrijf.Lineage}
                besluit={besluit}
                besluitGrondslagen={besluitGrondslagen.data?.objects?.filter(
                  (grondslag) => grondslag.Besluit === besluit.ID
                )}
              />
            </li>
          );
        })}
      </ul>

      {(vigerendBesluiten.data?.num_pages ?? 0) > 1 ? (
        <div className="mt-5 flex justify-center">
          <PaginationLinks
            aria-label="Besluiten pagina's"
            renderLink={({ children, page }) => (
              <Link
                to="/$bedrijfLineageId/bedrijf/besluiten"
                params={{ bedrijfLineageId: bedrijf.Lineage }}
                search={(old) => ({ ...old, p: page })}
              >
                {children}
              </Link>
            )}
            currentPage={search.p}
            totalPages={vigerendBesluiten.data?.num_pages ?? 0}
          />
        </div>
      ) : null}
    </>
  );
};
